import { ERROR_CODES, serverIdOf, userError } from './userError';
// Helpers for email sign-in links (magic links).
//
// Links look like https://new.lunch.team/MagicLogin/<token>. Older links may carry
// ?token=<token> instead. The page never verifies a token on load: mail scanners open
// links before people do, and a single-use token would be spent before the click.
import {
  withPendingSupportLink,
  withoutSupportLinkInPath,
} from '../support/deepLink';

// UUIDs today (36 chars with hyphens); base64url 256-bit tokens later (43 chars).
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

export const MAGIC_LINK_CLIENT_APP = 'LUNCH_WEB';

export function isValidMagicLinkToken(token) {
  return typeof token === 'string' && TOKEN_PATTERN.test(token);
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
}

// Returns the token from /MagicLogin/<token> or ?token=<token>, or '' when there is none.
export function extractMagicLinkToken(location) {
  const pathname = (location && location.pathname) || '';
  const search = (location && location.search) || '';

  const match = pathname.match(/\/MagicLogin\/([^/?#]+)\/?$/i);
  if (match) {
    return safeDecode(match[1]).trim();
  }

  const queryMatch = search.match(/[?&]token=([^&#]*)/);
  if (queryMatch) {
    return safeDecode(queryMatch[1]).trim();
  }

  return '';
}

export const MAGIC_LINK_ERRORS = {
  EXPIRED: 'expired',
  INVALID: 'invalid',
  THROTTLED: 'throttled',
  NOT_ALLOWED: 'not_allowed',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
};

const MESSAGES = {
  [MAGIC_LINK_ERRORS.EXPIRED]:
    'This sign-in link has expired. Links work for a short time only. Ask for a new link below, or sign in with your password.',
  [MAGIC_LINK_ERRORS.INVALID]:
    'This sign-in link has already been used or is not valid. Each link works once. Ask for a new link below, or sign in with your password.',
  [MAGIC_LINK_ERRORS.THROTTLED]:
    'Too many attempts. Please wait a few minutes, or sign in with your password.',
  [MAGIC_LINK_ERRORS.NOT_ALLOWED]:
    'Email sign-in links are not available for this account. Please sign in with your password.',
  [MAGIC_LINK_ERRORS.NETWORK]:
    'We could not reach Lunch.Team. Check your connection and try again.',
  [MAGIC_LINK_ERRORS.UNKNOWN]:
    "We couldn't finish signing you in. Try the link again, or sign in with your password.",
};

const CODES = {
  [MAGIC_LINK_ERRORS.EXPIRED]: ERROR_CODES.AUTH_LINK_EXPIRED,
  [MAGIC_LINK_ERRORS.INVALID]: ERROR_CODES.AUTH_LINK_USED,
  [MAGIC_LINK_ERRORS.THROTTLED]: ERROR_CODES.AUTH_LOCKED,
  [MAGIC_LINK_ERRORS.NOT_ALLOWED]: ERROR_CODES.AUTH_LINK_NOT_ALLOWED,
};

/**
 * The support reference for a failed sign-in link (src/utils/userError.js): code, time and the
 * server's id. Network and server failures keep their own codes (offline, unreachable, down).
 */
export function magicLinkUserError(kind, error) {
  const known = CODES[kind];
  return userError(known ? null : error, {
    area: 'AUTH',
    code: known,
    serverId: known ? serverIdOf(error) : undefined,
  });
}

export function magicLinkErrorMessage(kind) {
  return MESSAGES[kind] || MESSAGES[MAGIC_LINK_ERRORS.UNKNOWN];
}

function errorStatus(error) {
  if (!error) {
    return undefined;
  }
  if (typeof error.status === 'number') {
    return error.status;
  }
  if (error.response && typeof error.response.status === 'number') {
    return error.response.status;
  }
  return undefined;
}

function errorText(error) {
  const data = (error && error.data) || {};
  return [data.message, data.error, data.detail, data.code]
    .filter(value => typeof value === 'string')
    .join(' ')
    .toLowerCase();
}

// Turns a failed verify call into one of MAGIC_LINK_ERRORS. Never exposes backend text.
export function classifyMagicLinkError(error) {
  if (error && error.isNetworkError) {
    return MAGIC_LINK_ERRORS.NETWORK;
  }
  const status = errorStatus(error);
  if (status === 429) {
    return MAGIC_LINK_ERRORS.THROTTLED;
  }
  if (status === 403) {
    return MAGIC_LINK_ERRORS.NOT_ALLOWED;
  }
  if (status === 400 || status === 404 || status === 410 || status === 401) {
    return errorText(error).indexOf('expired') !== -1
      ? MAGIC_LINK_ERRORS.EXPIRED
      : MAGIC_LINK_ERRORS.INVALID;
  }
  return MAGIC_LINK_ERRORS.UNKNOWN;
}

// Message shown after "Email me a sign-in link". The same text whether or not the
// account exists, so the page does not reveal who has an account.
export const MAGIC_LINK_SENT_MESSAGE =
  'If there is a Lunch.Team account for that email, we have sent a sign-in link. It works once and expires soon. You can still sign in with your password.';

// How long a link works (backend default) and how long "Resend" stays disabled.
export const MAGIC_LINK_LIFETIME_MINUTES = 15;
export const MAGIC_LINK_RESEND_SECONDS = 30;
// Used when a 429 does not say how long to wait.
export const MAGIC_LINK_DEFAULT_RETRY_SECONDS = 60;

// Seconds to wait after a 429, from Retry-After (seconds or an HTTP date) or the body.
export function retryAfterSeconds(error, now = Date.now()) {
  if (!error) {
    return undefined;
  }
  const data = error.data || {};
  const candidates = [error.retryAfter, data.retryAfterSeconds, data.retryAfter];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    const asNumber = Number(value);
    if (!isNaN(asNumber) && isFinite(asNumber)) {
      return Math.max(1, Math.ceil(asNumber));
    }
    const asDate = Date.parse(value);
    if (!isNaN(asDate)) {
      return Math.max(1, Math.ceil((asDate - now) / 1000));
    }
  }
  return undefined;
}

export function formatWait(seconds) {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function isThrottled(error) {
  return errorStatus(error) === 429;
}

export function magicLinkRequestErrorMessage(error) {
  if (error && error.isNetworkError) {
    return MESSAGES[MAGIC_LINK_ERRORS.NETWORK];
  }
  if (isThrottled(error)) {
    const wait = retryAfterSeconds(error);
    return wait
      ? `Too many link requests. Please try again in ${formatWait(wait)}, or use your password.`
      : 'Too many link requests. Please wait a few minutes, or use your password.';
  }
  return 'We could not send a sign-in link just now. Please try again, or use your password.';
}

// "fehin@lunch.team" -> "fe•••@lunch.team". Shown on screen only, never sent anywhere.
export function maskEmail(email) {
  const value = (email || '').trim();
  const at = value.lastIndexOf('@');
  if (at < 1) {
    return value;
  }
  const local = value.slice(0, at);
  const visible = local.length <= 2 ? local.slice(0, 1) : local.slice(0, 2);
  return `${visible}•••${value.slice(at)}`;
}

// Only same-site paths, so ?redirect= can never send someone to another site.
export function safeRedirectPath(value) {
  if (typeof value !== 'string') {
    return '';
  }
  const path = value.trim();
  if (!/^\/[A-Za-z0-9]/.test(path) || path.indexOf('//') === 0 || path.indexOf('\\') !== -1) {
    return '';
  }
  if (/^\/MagicLogin\b/i.test(path)) {
    return '';
  }
  return path;
}

// Remembers (on this device only) which email asked for a link and where they were
// going, so /MagicLogin can prefill "Email me a new link" and redirect after sign-in.
// The link opens in another tab, so a support reply email's link waiting in this one
// goes along in the redirect (deepLink.js).
const CONTEXT_KEY = 'lt:magicLinkContext';

export function rememberMagicLinkContext({ email, redirect }) {
  try {
    localStorage.setItem(
      CONTEXT_KEY,
      JSON.stringify({
        email: (email || '').trim(),
        redirect: withPendingSupportLink(safeRedirectPath(redirect)),
      })
    );
  } catch (error) {
    // Storage can be unavailable (private mode); the flow still works without it.
  }
}

// Once a sign-in link has been used, the support conversation in the remembered redirect is done with:
// the context stays in localStorage, and a later sign-in on this device (maybe another customer's) must
// not go there again.
export function forgetSupportLinkInContext() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONTEXT_KEY) || 'null');
    if (!parsed || typeof parsed.redirect !== 'string') return;
    const redirect = withoutSupportLinkInPath(parsed.redirect);
    if (redirect !== parsed.redirect) {
      localStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...parsed, redirect }));
    }
  } catch (error) {
    // Storage can be unavailable (private mode).
  }
}

export function readMagicLinkContext() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CONTEXT_KEY) || '{}') || {};
    return {
      email: typeof parsed.email === 'string' ? parsed.email : '',
      redirect: safeRedirectPath(parsed.redirect),
    };
  } catch (error) {
    return { email: '', redirect: '' };
  }
}

export function isPhoneUserAgent(userAgent) {
  return /Android|iPhone|iPad|iPod/i.test(userAgent || '');
}



// WEBPACK FOOTER //
// ./src/utils/magicLink.js